import type { PoseTrack, StagePhases } from '@/shared/lib/sticky-scroll';

/**
 * 홈 히어로 카드 3장의 스크롤 궤적 — 계산은 공용 엔진(shared/lib/sticky-scroll)이 한다.
 *
 * 가운데 포개져 있던 카드가 부채처럼 펼쳐지고(spread), 이어서 서로 다른 속도로 떠오른다(drift).
 * 뒤쪽 대형 타이포 두 줄은 떠오르기 단계에서 반대 방향으로 흐른다.
 */

/** 카드가 서는 자리. 카드 데이터와 궤적을 배열 순서가 아니라 이 이름으로 잇는다 */
export type CardSlot = 'left' | 'center' | 'right';

/** 뒤쪽 대형 타이포가 떠오르기 단계 끝까지 흐르는 거리(vw) */
const LINE_DRIFT_VW = 7;

/**
 * 펼친 자세의 각도는 레퍼런스처럼 좌우가 바깥으로, 가운데가 살짝 반대로 기울게 둔다 —
 * 셋이 같은 방향이면 한 장이 기운 것처럼 보인다.
 * 떠오르기의 y 는 셋 다 달라야 한다(스펙이 못 박는다) — 같으면 한 덩어리로 움직인다.
 * 가운데 카드가 가장 많이 뜨되 카드 높이의 14% 를 넘기지 않는다 — 더 뜨면 위의
 * WELCOME 배지를 덮는다(1440×900 기준으로 확인).
 */
export const CARD_TRACKS: Readonly<Record<CardSlot, PoseTrack>> = {
  left: {
    stacked: { x: 20, y: 16, rotate: -10, scale: 0.86 },
    spread: { x: -108, y: -2, rotate: 5, scale: 1 },
    drift: { x: -116, y: -10, rotate: 9, scale: 1 },
  },
  center: {
    stacked: { x: 0, y: 20, rotate: 2, scale: 0.9 },
    spread: { x: 0, y: 2, rotate: -2.5, scale: 1 },
    drift: { x: 0, y: -12, rotate: -5, scale: 1 },
  },
  right: {
    stacked: { x: -20, y: 16, rotate: 10, scale: 0.86 },
    spread: { x: 108, y: 0, rotate: 3.5, scale: 1 },
    drift: { x: 116, y: -5, rotate: 7, scale: 1 },
  },
};

/** 대형 타이포 한 줄의 가로 이동(vw). 짝수 줄은 왼쪽, 홀수 줄은 오른쪽으로 흐른다 */
export function lineShift(lineIndex: number, drift: number): number {
  const direction = lineIndex % 2 === 0 ? -1 : 1;
  // -0 을 0 으로 — 시작 상태가 `translateX(-0vw)` 로 새지 않게 한다
  return direction * LINE_DRIFT_VW * Math.min(1, Math.max(0, drift)) + 0;
}

/** useStickyScroll 에 넘기는 타이포 transform — 모듈 상수여야 구독을 다시 걸지 않는다 */
export const heroLineTransform = (index: number, { drift }: StagePhases): string =>
  `translateX(${lineShift(index, drift)}vw)`;
